import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useApp } from '@/context/AppContext';
import { useAuth } from '@/context/AuthContext';
import { useOffline } from '@/context/OfflineContext';
import { useCurrency, useCan } from '@/context/SettingsContext';
import { API_URL as __API_BASE__ } from '@/lib/apiBase';
const API_URL = __API_BASE__;

import {
  ShoppingCart,
  Clock,
  ChartLineUp,
  Table,
  ForkKnife,
  Package,
  Users,
  UserCircle,
  Gear,
  MonitorPlay,
  ArrowRight,
  Hourglass,
} from '@phosphor-icons/react';

/**
 * Neon Function Dashboard
 * Displays every major module of Restro POS as a glowing neon card.
 * Each card is role-aware (hidden if the user lacks access).
 */
export const DashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentOutlet, getDashboardStats, fetchOrders, orders, tables, menuItems } = useApp();
  const { isOnline, pendingCount } = useOffline();
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [kdsOfflineCount, setKdsOfflineCount] = useState(0);

  useEffect(() => {
    if (!currentOutlet) return undefined;
    // Reset numbers immediately so we don't flash the previous outlet's data
    setStats(null);
    setKdsOfflineCount(0);
    setStatsLoading(true);

    let cancelled = false;
    const outletId = currentOutlet.id;

    const loadData = async () => {
      try {
        const [statsData] = await Promise.all([getDashboardStats(), fetchOrders()]);
        // Ignore the response if the outlet changed mid-flight
        if (cancelled || outletId !== currentOutlet.id) return;
        setStats(statsData);
      } finally {
        if (!cancelled) setStatsLoading(false);
      }
    };

    loadData();
    const interval = setInterval(loadData, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [currentOutlet, getDashboardStats, fetchOrders]);

  // Poll KDS station status for the offline-station badge on the KDS card
  useEffect(() => {
    if (!currentOutlet) return;    const fetchStatus = async () => {
      try {
        const r = await axios.get(
          `${API_URL}/api/kds-stations/status?outlet_id=${currentOutlet.id}`,
          { withCredentials: true }
        );
        const offline = (r.data || []).filter((s) => !s.online).length;
        setKdsOfflineCount(offline);
      } catch {
        // ignore — likely offline
      }
    };
    fetchStatus();
    const t = setInterval(fetchStatus, 15000);
    return () => clearInterval(t);
  }, [currentOutlet]);

  const role = user?.role;
  const has = (roles) => roles.includes(role);

  // Per-card permission gating (configurable via Access Control tab).
  // Each card has a primary permission; the dashboard hides cards the user lacks access to.
  const canPOS = useCan('orders.create');
  const canKDSUpdate = useCan('kds.update_status');
  const canOrdersView = useCan('orders.view');
  const canTables = useCan('tables.manage');
  const canMenuView = useCan('menu.view');
  const canInventory = useCan('inventory.view');
  const canCustomers = useCan('customers.view');
  const canReports = useCan('reports.view');
  const canUsers = useCan('users.manage');
  const canSettings = useCan('settings.edit');
  const canKDS = canKDSUpdate || canOrdersView;
  const canTimesheet = true; // own timesheet always allowed

  const activeOrders = orders.filter((o) =>
    ['new', 'confirmed', 'preparing', 'ready'].includes(o.status)
  ).length;
  const occupiedTables = tables.filter((t) => t.status === 'occupied').length;
  const availableTables = tables.filter((t) => t.status === 'available').length;

  // Show a dash while we're loading data for a new outlet so numbers don't flash stale values
  const dash = statsLoading || !stats;
  const D = (v) => (dash ? '—' : v);

  // 10 function cards, each with its own neon hue (RGB)
  const cards = [
    {
      key: 'pos',
      title: 'POS',
      subtitle: 'Point of Sale',
      icon: ShoppingCart,
      metric: D(stats?.today_orders ?? 0),
      unit: 'Orders Today',
      footer: dash ? 'Loading…' : `$${(stats?.today_sales || 0).toFixed(0)} in sales`,
      neon: '34, 211, 238', // cyan
      path: '/pos',
      can: canPOS,
      roles: ['super_admin', 'company_admin', 'outlet_manager', 'cashier', 'server'],
    },
    {
      key: 'kds',
      title: 'KDS',
      subtitle: 'Kitchen Display',
      icon: MonitorPlay,
      metric: D(activeOrders),
      unit: 'Active',
      footer: kdsOfflineCount > 0 ? `${kdsOfflineCount} station${kdsOfflineCount > 1 ? 's' : ''} offline` : 'Real-time tickets',
      footerAlert: kdsOfflineCount > 0,
      neon: '74, 222, 128', // green
      path: '/kds',
      can: canKDS,
      roles: ['super_admin', 'company_admin', 'outlet_manager', 'kitchen_staff', 'expediter'],
    },
    {
      key: 'tables',
      title: 'Tables',
      subtitle: 'Floor Management',
      icon: Table,
      metric: dash ? '—' : `${occupiedTables}/${tables.length || 0}`,
      unit: 'Occupied',
      footer: dash ? 'Loading…' : `${availableTables} available`,
      neon: '167, 139, 250', // violet
      path: '/tables',
      roles: ['super_admin', 'company_admin', 'outlet_manager', 'server'],
    },
    {
      key: 'orders',
      title: 'Orders',
      subtitle: 'Order History',
      icon: Clock,
      metric: D(orders.length),
      unit: 'Total',
      footer: dash ? 'Loading…' : `${activeOrders} in progress`,
      neon: '251, 191, 36', // amber
      path: '/orders',
      can: canOrdersView,
      roles: ['super_admin', 'company_admin', 'outlet_manager', 'cashier', 'server', 'kitchen_staff', 'expediter', 'inventory_manager'],
    },
    {
      key: 'menu',
      title: 'Menu',
      subtitle: 'Items & Modifiers',
      icon: ForkKnife,
      metric: D(menuItems.length),
      unit: 'Items',
      footer: 'Manage catalog',
      neon: '249, 115, 22', // orange
      path: '/menu',
      can: canMenuView,
      roles: ['super_admin', 'company_admin', 'outlet_manager'],
    },
    {
      key: 'inventory',
      title: 'Inventory',
      subtitle: 'Stock & Recipes',
      icon: Package,
      metric: D(stats?.low_stock_count ?? 0),
      unit: 'Low Stock',
      footer: 'Track ingredients',
      neon: '244, 114, 182', // pink
      path: '/inventory',
      can: canInventory,
      roles: ['super_admin', 'company_admin', 'outlet_manager', 'inventory_manager'],
    },
    {
      key: 'customers',
      title: 'Customers',
      subtitle: 'Loyalty & CRM',
      icon: UserCircle,
      metric: D(stats?.customer_count ?? 0),
      unit: 'Profiles',
      footer: 'Promotions & points',
      neon: '56, 189, 248', // sky blue
      path: '/customers',
      can: canCustomers,
      roles: ['super_admin', 'company_admin', 'outlet_manager', 'cashier'],
    },
    {
      key: 'reports',
      title: 'Reports',
      subtitle: 'Analytics',
      icon: ChartLineUp,
      metric: dash ? '—' : `$${(stats?.today_sales || 0).toFixed(0)}`,
      unit: 'Today',
      footer: 'Deep insights',
      neon: '129, 140, 248', // indigo
      path: '/reports',
      can: canReports,
      roles: ['super_admin', 'company_admin', 'outlet_manager'],
    },
    {
      key: 'users',
      title: 'Users',
      subtitle: 'Staff & Roles',
      icon: Users,
      metric: D(stats?.user_count ?? '—'),
      unit: 'Staff',
      footer: 'Access control',
      neon: '248, 113, 113', // red
      path: '/users',
      can: canUsers,
      roles: ['super_admin', 'company_admin', 'outlet_manager'],
    },
    {
      key: 'timesheet',
      title: 'Timesheet',
      subtitle: 'Clock & Shifts',
      icon: Hourglass,
      metric: D(stats?.staff_on_clock ?? '—'),
      unit: 'On Clock',
      footer: 'Time tracking',
      neon: '52, 211, 153', // emerald
      path: '/timesheet',
      can: canTimesheet,
      roles: ['super_admin', 'company_admin', 'outlet_manager', 'cashier', 'server', 'kitchen_staff', 'expediter', 'inventory_manager'],
    },
    {
      key: 'settings',
      title: 'Settings',
      subtitle: 'System Controls',
      icon: Gear,
      metric: '5',
      unit: 'Themes',
      footer: 'Configure & theme',
      neon: '239, 68, 68', // crimson
      path: '/settings',
      can: canSettings,
      roles: ['super_admin', 'company_admin', 'outlet_manager'],
    },
  ];

  // Show a card if BOTH the role list allows it AND the permission grants access.
  const visibleCards = cards.filter((c) => has(c.roles) && c.can !== false);

  // Parallax tilt-on-hover
  const handleTiltMove = (e) => {
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;   // 0..1
    const y = (e.clientY - rect.top) / rect.height;   // 0..1
    const rotY = (x - 0.5) * 12;   // -6deg..+6deg
    const rotX = (0.5 - y) * 12;
    el.style.setProperty('--tilt-x', `${rotX}deg`);
    el.style.setProperty('--tilt-y', `${rotY}deg`);
    el.style.setProperty('--glare-x', `${x * 100}%`);
    el.style.setProperty('--glare-y', `${y * 100}%`);
    el.classList.add('is-tilting');
  };
  const handleTiltLeave = (e) => {
    const el = e.currentTarget;
    el.style.setProperty('--tilt-x', '0deg');
    el.style.setProperty('--tilt-y', '0deg');
    el.classList.remove('is-tilting');
  };

  if (!currentOutlet) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Please select an outlet to view dashboard</p>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page">
      <div className="neon-dashboard" data-testid="neon-dashboard">
        {/* Header */}
        <div className="relative z-10 mb-8 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="neon-heading text-3xl sm:text-4xl font-extrabold uppercase">
              Restro POS
            </h1>
            <p className="text-white/60 mt-1 tracking-wider text-sm uppercase">
              {currentOutlet?.name} — Command Center
            </p>
          </div>
          <div className="neon-pill neon-pill-live" style={{ '--neon': isOnline ? '74, 222, 128' : '239, 68, 68' }}>
            {isOnline ? 'Live' : 'Offline'}
          </div>
        </div>

        {/* Offline banner */}
        {(!isOnline || pendingCount > 0) && (
          <div
            className="relative z-10 mb-6 rounded-xl border px-4 py-3 flex items-center justify-between"
            style={{
              borderColor: 'rgba(251, 191, 36, 0.5)',
              backgroundColor: 'rgba(251, 191, 36, 0.1)',
              color: '#fbbf24',
            }}
            data-testid="offline-banner"
          >
            <div className="text-sm">
              {!isOnline && <span className="font-semibold mr-2">You're offline.</span>}
              {pendingCount > 0
                ? `${pendingCount} order${pendingCount > 1 ? 's' : ''} queued — will auto-sync when connection returns.`
                : 'Cached menu data is being used. Orders will be queued locally and synced when the connection returns.'}
            </div>
          </div>
        )}

        {/* Neon function grid */}
        <div
          className="relative z-10 grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"
          data-testid="neon-function-grid"
        >
          {visibleCards.map((card, idx) => {
            const Icon = card.icon;
            return (
              <button
                key={card.key}
                onClick={() => navigate(card.path)}
                onMouseMove={handleTiltMove}
                onMouseLeave={handleTiltLeave}
                className="neon-card text-left"
                style={{ '--neon': card.neon, '--enter-delay': `${idx * 70}ms` }}
                data-testid={`neon-card-${card.key}`}
              >
                <span className="neon-card-glare" aria-hidden="true" />
                <div className="neon-card-icon">
                  <Icon size={36} weight="duotone" />
                </div>
                <h3 className="neon-card-title">{card.title}</h3>
                <div className="neon-card-divider" />
                <p className="neon-card-subtitle">{card.subtitle}</p>
                <div className="neon-card-metric">
                  {card.metric}
                  {card.unit && <span className="unit">{card.unit}</span>}
                </div>
                <div className="neon-card-footer">
                  <span style={card.footerAlert ? { color: '#fbbf24', fontWeight: 600 } : undefined}>
                    {card.footerAlert && '⚠ '}{card.footer}
                  </span>
                  <span className="neon-card-cta">
                    Open <ArrowRight size={14} weight="bold" />
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
